import { Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ModalController, ViewWillEnter } from '@ionic/angular';

import { AuthenticationService } from 'src/app/core/services/http/authentication.service';
import { ToastService } from 'src/app/core/services/behavior/toast.service';
import { ModalRoleEnum } from '../../enums/modal-role.enum';
import { MetodosModal } from '../../interfaces/metodos-modal.interface';
import { HelpersUtil } from '../../utils/helpers.util';
import { StorageService } from 'src/app/core/services/behavior/storage.service';

@Component({
    selector: 'app-configuracao-manual',
    templateUrl: './configuracao-manual.dialog.html',
    styleUrls: ['./configuracao-manual.dialog.scss']
})
export class ConfiguracaoManualDialog implements ViewWillEnter, MetodosModal, OnInit {
    @ViewChild('baseUrlInpt', { static: false })
    baseUrlInpt: ElementRef<HTMLInputElement>;

    public checkboxSolicitarSenha = false;
    public percentualAlturaMenuProdutos: number = 0;

    form = this.fb.group({
        baseUrl: ['', [Validators.required, Validators.pattern('^(http:\/\/|https:\/\/).*\/api.*$')]],
        databaseName: ['', [Validators.required, Validators.minLength(3)]],
        numeroCaixa: [1, [Validators.required, Validators.minLength(1)]],
        nomeTerminal: ['', [Validators.required, Validators.minLength(3)]],
        baseUrlQrCode: ['', [Validators.required, Validators.pattern('^(https?:\\/\\/)[\\w.-]+(:\\d+)?(\\/.*[^\\/])?$')]],
        whatsappResponsavel: ['', [Validators.pattern(/^$|^\d{11}$/)]],
        whatsappEmpresa: ['', [Validators.pattern(/^$|^\d{11}$/)]],
        tokenMFA: ['', [Validators.required, Validators.maxLength(10)]],
        solicitarSenhaSomentePrimeiraVez: [true],
    });

    constructor(
        private fb: FormBuilder,
        private renderer: Renderer2,
        private modalCtrl: ModalController,
        private toastService: ToastService,

        private authService: AuthenticationService,
        private storageService: StorageService
    ) { }

    ngOnInit(): void {
        this.percentualAlturaMenuProdutos = this._normalizarPercentualAlturaMenuProdutos(this.percentualAlturaMenuProdutos);
        this.carregarAlturaMenuProdutos();
    }

    async ionViewWillEnter() {
        HelpersUtil.executeAfterDelay(() => this.renderer.selectRootElement(this.baseUrlInpt.nativeElement).focus())

        this.baseUrlCtrl.setValue(this.authService.baseUrl ?? '');
        this.databaseNameCtrl.setValue(this.authService.databaseName ?? '');
        this.numeroCaixaCtrl.setValue(this.authService.numeroCaixa ?? 1);
        this.nomeTerminalCtrl.setValue(this.authService.nomeTerminal.length > 0 ? this.authService.nomeTerminal : `${Math.floor(Math.random() * 1000) + 1}-web`);
        this.baseUrlQrCodeCtrl.setValue(this.authService.baseUrlQrCode ?? 'https://cardapio.mastervariedades.com.br');

        const updateQrUrlSuggestion = () => {
            if (this.baseUrlQrCodeCtrl.dirty) return; // Não sobrescreve se o usuário já editou o campo manualmente

            const baseUrl = this.baseUrlCtrl.value;
            const dbName = this.databaseNameCtrl.value;

            if (baseUrl && dbName) {
                try {
                    const hostname = new URL(baseUrl).hostname;
                    let dominio = hostname;

                    const parts = hostname.split('.');
                    if (parts.length > 2 && isNaN(Number(parts[parts.length - 1]))) {
                        dominio = parts.slice(1).join('.');
                    }

                    const suggestedUrl = `https://${dbName}.${dominio}`;
                    this.baseUrlQrCodeCtrl.setValue(suggestedUrl, { emitEvent: false });
                } catch (e) {
                    // url parse error, do nothing
                }
            }
        };

        this.baseUrlCtrl.valueChanges.subscribe(updateQrUrlSuggestion);
        this.databaseNameCtrl.valueChanges.subscribe(updateQrUrlSuggestion);

        var a = await this.storageService.get('solicitarSenhaSomentePrimeiraVez');
        this.checkboxSolicitarSenha = a ?? true;

        this.solicitarSenhaSomentePrimeiraVezCtrl.setValue(this.checkboxSolicitarSenha);
    }

    private carregarAlturaMenuProdutos() {
        const alturaMenuSaved = localStorage.getItem("percentualAltura");
        if (!!alturaMenuSaved) {
            this.percentualAlturaMenuProdutos = Number(alturaMenuSaved);
        } else {
            this.percentualAlturaMenuProdutos = 25;
        }
    }

    public atualizarPercentualAlturaMenuProdutos(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.percentualAlturaMenuProdutos = this._normalizarPercentualAlturaMenuProdutos(input.value);
        input.value = `${this.percentualAlturaMenuProdutos}`;
        localStorage.setItem("percentualAltura", this.percentualAlturaMenuProdutos.toString());
    }

    private _normalizarPercentualAlturaMenuProdutos(valor: number | string | null | undefined): number {
        const percentual = Number(valor);

        if (Number.isNaN(percentual)) {
            return 25;
        }

        return Math.min(100, Math.max(5, Math.round(percentual)));
    }

    isDragging = false;
    importedData: any = {};
    clientId?: string;

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.name.endsWith('.json') || file.type === 'application/json') {
                this.lerArquivo(file);
            } else {
                this.exibirErroArquivo();
            }
        }
    }

    importarJson(event: any): void {
        const file = event.target.files[0];
        if (file) {
            this.lerArquivo(file);
        }
    }

    private lerArquivo(file: File) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
            try {
                const json = JSON.parse(e.target.result);
                this.importedData = json;
                if (json.baseUrl) this.baseUrlCtrl.setValue(json.baseUrl);
                if (json.databaseName) this.databaseNameCtrl.setValue(json.databaseName);
                if (json.clientId) {
                    this.clientId = json.clientId;
                    this.form.controls['tokenMFA'].clearValidators();
                    this.form.controls['tokenMFA'].updateValueAndValidity();
                }

                this.toastService.success('Arquivo importado com sucesso!');
            } catch (error) {
                console.error('Erro ao processar o arquivo JSON', error);
                this.exibirErroArquivo();
            }
        };
        reader.readAsText(file);
    }

    private exibirErroArquivo() {
        this.toastService.error('Arquivo JSON inválido ou não suportado.');
    }

    private get baseUrlCtrl() {
        return this.form.controls['baseUrl'];
    }

    private get databaseNameCtrl() {
        return this.form.controls['databaseName'];
    }

    private get numeroCaixaCtrl() {
        return this.form.controls['numeroCaixa'];
    }

    private get nomeTerminalCtrl() {
        return this.form.controls['nomeTerminal'];
    }

    private get baseUrlQrCodeCtrl() {
        return this.form.controls['baseUrlQrCode'];
    }

    private get solicitarSenhaSomentePrimeiraVezCtrl() {
        return this.form.controls['solicitarSenhaSomentePrimeiraVez'];
    }

    private get whatsappResponsavelCtrl() {
        return this.form.controls['whatsappResponsavel'];
    }

    private get whatsappEmpresaCtrl() {
        return this.form.controls['whatsappEmpresa'];
    }

    async cancel(): Promise<void> {
        await this.modalCtrl.dismiss(null, ModalRoleEnum.CANCEL);
    }

    async confirm(): Promise<void> {
        const dataClose = {
            ...this.form.value,
            clientId: this.clientId
        } as unknown as ConfiguracaoManualDataClose;
        await this.modalCtrl.dismiss(dataClose, ModalRoleEnum.CONFIRM);
    }
}

export class ConfiguracaoManualDataClose {
    constructor(
        public baseUrl: string,
        public databaseName: string,
        public numeroCaixa: number,
        public solicitarSenhaSomentePrimeiraVez: boolean,
        public baseUrlQrCode: string,
        public nomeTerminal: string,
        public whatsappResponsavel: string,
        public whatsappEmpresa: string,
        public tokenMFA: string,
        public clientId?: string,
    ) { }
}